/**
 * Presentation-only locale bridge for Judge.me.
 *
 * Judge.me remains responsible for rendering, submissions, media uploads,
 * verification, moderation and structured data. This module only normalizes
 * known interface strings to Shopify's active storefront locale.
 */

export const JUDGEME_ROOT_SELECTOR = [
  '#judgeme_product_reviews',
  '.jdgm-review-widget-modal',
  '.jdgm-write-review-modal',
  '.jdgm-preview-badge',
  '.jdgm-all-reviews-widget',
  '.jdgm-revs-tab',
].join(', ');

export const PROTECTED_CONTENT_SELECTOR = [
  '.jdgm-rev',
  '.jdgm-review-card',
  '.jm-review-card',
  '.jm-review-item',
  '[data-review-id]',
  '.jdgm-rev__body',
  '.jdgm-rev__title',
  '.jdgm-rev__author',
  '.jdgm-rev__reply',
  '.jdgm-write-review-modal__product-title',
].join(', ');

const TRANSLATION_PAIRS = [
  ['Customer Reviews', 'Klientide arvustused'],
  ['Reviews', 'Arvustused'],
  ['Be the first to write a review', 'Ole esimene, kes kirjutab arvustuse'],
  ['Write a review', 'Kirjuta arvustus'],
  ['How would you rate this product?', 'Kuidas hindaksite seda toodet?'],
  ["We'd love it if you shared your experience.", 'Meil oleks hea meel, kui jagaksite oma kogemust.'],
  ['Review content (Required)', 'Arvustuse sisu (Nõutud)'],
  ['Start writing here...', 'Alusta kirjutamist siin...'],
  ['About you', 'Teie kohta'],
  ['Email address (Required)', 'E-posti aadress (Nõutud)'],
  ['Your email address', 'Teie e-posti aadress'],
  ['Display name (Required)', 'Kuvatav nimi (Nõutud)'],
  ['Display name', 'Kuvatav nimi'],
  ['Anonymous review', 'Arvustus anonüümne'],
  ['Anonymous review', 'Anonüümne arvustus'],
  ['Share a photo or video', 'Jaga pilti või videot'],
  ['Paste your YouTube URL here', 'Kleebi siia oma YouTube URL'],
  [
    "We'll only contact you about your review if necessary. By submitting your review, you agree to our",
    'Võtame teiega ühendust ainult teie arvustuse kohta, kui see on vajalik. Arvustuse esitamisega nõustute meie',
  ],
  [
    "We'll only contact you about your review if necessary. By submitting your review, you agree to our",
    'Võtame teiega teie arvustuse osas ühendust ainult vajadusel. Arvustuse esitamisega nõustute meie',
  ],
  ['Terms', 'tingimuste'],
  ['and', 'ja'],
  ['Privacy Policy', 'privaatsuspoliitikaga'],
  ['Please tell us a little more about yourself.', 'Palun rääkige meile endast rohkem.'],
  ['Please tell us a little more about yourself.', 'Palun rääkige meile endast veidi lähemalt.'],
  ['We respect your privacy.', 'Me täname teie privaatsust.'],
  ['We respect your privacy.', 'Austame teie privaatsust.'],
  ['This will not be visible to other customers.', 'See ei ole teistele klientidele nähtav.'],
  ['Upload a photo or video to support your review.', 'Laadige üles foto või video, et toetada oma arvustust.'],
  ['Paste your YouTube URL here', 'Kleebi siia oma Youtube URL'],
  ['Click to upload', 'Klõpsake üleslaadimiseks'],
  ['or drag and drop', 'või pukseerige ja kukutage'],
  ['or drag and drop', 'või lohistage'],
  ["We're processing it and it will appear in the store soon.", 'Töötleme seda ja see ilmub poodi varsti.'],
  [
    'Please verify your email address by clicking the link we just sent you. This helps us keep reviews authentic.',
    'Palun kinnitage oma e-posti aadress, klõpsates meie poolt just saadetud lingil. See aitab meil hoida arvustused autentsetena.',
  ],
  [
    'Your feedback helps us improve. Please share your thoughts with us.',
    'Meie arvustused aitavad meil paremaks muutma. Palun jagage meile oma mõtteid.',
  ],
  [
    'Your feedback helps us improve. Please share your thoughts with us.',
    'Teie tagasiside aitab meil paremaks muutuda. Palun jagage meiega oma mõtteid.',
  ],
  ['Poor', 'Halb'],
  ['Great', 'Hea'],
  ['Back', 'Tagasi'],
  ['Next', 'Järgmine'],
  ['Submit Review', 'Esita arvustus'],
  ['Thank you for your review!', 'Täname teid arvustuse eest!'],
  ['Would you like to share your shopping experience with us?', 'Kas soovite meiega oma ostukogemust jagada?'],
  ['Would you like to share your shopping experience with us?', 'Kas soovite oma ostukogemusest meiega jagada?'],
  ['Would you like to share your shopping experience with us?', 'Kas soovite meiega oma ostukogemust jagada?'],
  ['Close', 'Sulge'],
  ['Verified', 'Kinnitatud'],
  ['Verified Buyer', 'Kinnitatud ostja'],
  ['Helpful', 'Kasulik'],
  ['See more', 'Vaata rohkem'],
  ['See less', 'Vaata vähem'],
  ['Load more', 'Laadi rohkem'],
  ['Most Recent', 'Uusimad'],
  ['Highest Rating', 'Kõrgeim hinnang'],
  ['Lowest Rating', 'Madalaim hinnang'],
  ['Pictures First', 'Piltidega arvustused enne'],
  ['Videos First', 'Videotega arvustused enne'],
  ['Search reviews', 'Otsi arvustusi'],
  ['Sort by', 'Sorteeri'],
  ['Clear', 'Tühjenda'],
  ['No reviews yet', 'Arvustusi pole veel'],
  ['See all reviews', 'Vaata kõiki arvustusi'],
  ['Close review form', 'Sulge arvustuse vorm'],
  ['Review form pages', 'Arvustuse vormi lehed'],
  ['Rating', 'Hinnang'],
  ['Privacy and Terms', 'Privaatsus ja tingimused'],
  ['Review form navigation', 'Arvustuse vormil liikumine'],
  ['Previous step', 'Eelmine samm'],
  ['Next step', 'Järgmine samm'],
  ['1 star', '1 tärn'],
  ['2 stars', '2 tärni'],
  ['3 stars', '3 tärni'],
  ['4 stars', '4 tärni'],
  ['5 stars', '5 tärni'],
];

const translations = {
  en: new Map(),
  et: new Map(),
};

for (const [english, estonian] of TRANSLATION_PAIRS) {
  translations.en.set(english, english);
  translations.en.set(estonian, english);
  translations.et.set(english, estonian);
  translations.et.set(estonian, estonian);
}

const LEGACY_ESTONIAN_ALIASES = [
  [
    'Võtame teiega ühendust ainult teie arvustuse kohta, kui see on vajalik. Arvustuse esitamisega nõustute meie',
    'Võtame teiega teie arvustuse osas ühendust ainult vajadusel. Arvustuse esitamisega nõustute meie',
  ],
  ['Palun rääkige meile endast rohkem.', 'Palun rääkige meile endast veidi lähemalt.'],
  ['Me täname teie privaatsust.', 'Austame teie privaatsust.'],
  ['Arvustus anonüümne', 'Anonüümne arvustus'],
  ['või pukseerige ja kukutage', 'või lohistage'],
  ['Kas soovite oma ostukogemusest meiega jagada?', 'Kas soovite meiega oma ostukogemust jagada?'],
  [
    'Meie arvustused aitavad meil paremaks muutma. Palun jagage meile oma mõtteid.',
    'Teie tagasiside aitab meil paremaks muutuda. Palun jagage meiega oma mõtteid.',
  ],
];

for (const [legacyEstonian, preferredEstonian] of LEGACY_ESTONIAN_ALIASES) {
  translations.et.set(legacyEstonian, preferredEstonian);
}

/** @param {string} text @param {string} locale */
export function translateInterfaceText(text, locale) {
  const reviewCount = text.match(/^(\d+)\s+(?:review|reviews|arvustus|arvustust)$/i);
  if (reviewCount && translations[locale]) {
    const count = reviewCount[1];
    const singular = count === '1';
    if (locale === 'et') return `${count} ${singular ? 'arvustus' : 'arvustust'}`;
    return `${count} ${singular ? 'review' : 'reviews'}`;
  }

  return translations[locale]?.get(text) ?? text;
}

/** @param {Element | null | undefined} element */
function isProtectedContent(element) {
  return Boolean(element?.closest?.(PROTECTED_CONTENT_SELECTOR));
}

/** @param {Text} node @param {string} locale */
export function translateTextNode(node, locale) {
  if (!translations[locale] || isProtectedContent(node.parentElement)) return false;

  const original = node.nodeValue ?? '';
  const trimmed = original.trim();
  const translated = translateInterfaceText(trimmed, locale);
  if (!trimmed || translated === trimmed) return false;

  const leadingWhitespace = original.match(/^\s*/)?.[0] ?? '';
  const trailingWhitespace = original.match(/\s*$/)?.[0] ?? '';
  node.nodeValue = `${leadingWhitespace}${translated}${trailingWhitespace}`;
  return true;
}

const PRIVACY_NOTICE_SELECTOR = '.jdgm-write-review-modal__gdpr-statement';

const PRIVACY_NOTICES = {
  en: {
    ariaLabel: 'Privacy and Terms',
    parts: [
      "We'll only contact you about your review if necessary. By submitting your review, you agree to the ",
      { text: 'Judge.me Terms of Service', href: 'https://judge.me/terms' },
      '. Your personal data will be processed as described in the ',
      { text: 'Judge.me Privacy Policy', href: 'https://judge.me/privacy' },
      ' and ',
      { text: 'LIORE Privacy Policy', href: '/en/policies/privacy-policy' },
      '.',
    ],
  },
  et: {
    ariaLabel: 'Privaatsus ja tingimused',
    parts: [
      'Võtame teiega teie arvustuse osas ühendust ainult vajadusel. Arvustuse esitamisega nõustute ',
      { text: 'Judge.me kasutustingimustega', href: 'https://judge.me/terms' },
      '. Teie isikuandmeid töödeldakse ',
      { text: 'Judge.me privaatsuspoliitika', href: 'https://judge.me/privacy' },
      ' ja ',
      { text: 'LIORE privaatsuspoliitika', href: '/policies/privacy-policy' },
      ' kohaselt.',
    ],
  },
};

/** @param {Element} root @param {string} locale */
export function normalizePrivacyNotice(root, locale) {
  const notice = PRIVACY_NOTICES[locale];
  const statement = root.matches?.(PRIVACY_NOTICE_SELECTOR)
    ? root
    : root.querySelector?.(PRIVACY_NOTICE_SELECTOR);
  if (!notice || !statement || statement.dataset.liorePrivacyNotice === locale) return false;

  const nodes = notice.parts.map((part) => {
    if (typeof part === 'string') return statement.ownerDocument.createTextNode(part);

    const link = statement.ownerDocument.createElement('a');
    link.textContent = part.text;
    link.setAttribute('href', part.href);
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'nofollow noopener');
    return link;
  });

  statement.replaceChildren(...nodes);
  statement.setAttribute('aria-label', notice.ariaLabel);
  statement.dataset.liorePrivacyNotice = locale;
  return true;
}

/** @param {Element} element @param {string} locale */
function translateAttributes(element, locale) {
  if (isProtectedContent(element)) return;

  for (const attribute of ['aria-label', 'placeholder', 'title']) {
    const original = element.getAttribute(attribute);
    if (!original) continue;

    const translated = translateInterfaceText(original.trim(), locale);
    if (translated !== original.trim()) element.setAttribute(attribute, translated);
  }

  if (element instanceof HTMLInputElement && ['button', 'submit'].includes(element.type)) {
    const translated = translateInterfaceText(element.value.trim(), locale);
    if (translated !== element.value.trim()) element.value = translated;
  }
}

/** @param {Element} root @param {string} locale */
export function translateJudgeMeRoot(root, locale) {
  if (!translations[locale]) return;

  normalizePrivacyNotice(root, locale);

  const nodeFilter = root.ownerDocument.defaultView.NodeFilter;
  const walker = root.ownerDocument.createTreeWalker(root, nodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    translateTextNode(node, locale);
    node = walker.nextNode();
  }

  translateAttributes(root, locale);
  for (const element of root.querySelectorAll('[aria-label], [placeholder], [title], input[type="button"], input[type="submit"]')) {
    translateAttributes(element, locale);
  }
}

/** @param {Document} documentRoot @param {string} locale */
export function translateJudgeMe(documentRoot, locale) {
  for (const root of documentRoot.querySelectorAll(JUDGEME_ROOT_SELECTOR)) {
    translateJudgeMeRoot(root, locale);
  }
}

function initializeJudgeMeLocaleBridge() {
  const loader = document.querySelector('script[data-liore-judgeme-locale-bridge]');
  const locale = loader?.dataset.locale?.split('-')[0]?.toLowerCase() || document.documentElement.lang.split('-')[0];
  if (!translations[locale] || !document.body) return;

  let translationScheduled = false;
  const scheduleTranslation = () => {
    if (translationScheduled) return;
    translationScheduled = true;

    queueMicrotask(() => {
      translationScheduled = false;
      translateJudgeMe(document, locale);
    });
  };

  scheduleTranslation();
  new MutationObserver(scheduleTranslation).observe(document.body, { childList: true, subtree: true });
}

if (typeof document !== 'undefined') initializeJudgeMeLocaleBridge();
